import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Link } from 'react-router-dom';
import { businessInfo, operatorDisclosure } from '../utils/businessInfo';

export const TermsPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="Terms & Conditions - Swag Shots"
                description="Swag Shots Terms & Conditions - Legal terms governing the use of Swag Shots app and services."
                url="https://www.swagshots.com/terms"
            />

            <div className="pt-24 pb-16">
                <div className="container-custom max-w-4xl">
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
                        <div className="text-gray-600 mb-8">
                            <p>Effective Date: January 1, 2025</p>
                            <p className="mt-1">Operator: {businessInfo.operatorName}</p>
                            <p className="mt-1">GSTIN: {businessInfo.gstin}</p>
                        </div>

                        {/* Important Notice */}
                        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-orange-200 rounded-xl p-8 mb-8">
                            <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center">
                                <span className="text-3xl mr-3">⚠️</span>
                                Important Notice
                            </h3>
                            <p className="text-orange-700 leading-relaxed">
                                Please read these Terms carefully. They govern your use of Swag Shots and constitute a legally binding agreement between you and {businessInfo.operatorName}, the operator of Swag Shots. By using Swag Shots, you agree to these Terms and our Privacy Policy.
                            </p>
                        </div>

                        <div className="space-y-8">
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    Business Identity
                                </h2>
                                <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                                    <p className="text-gray-700 leading-relaxed">
                                        {operatorDisclosure} References to "Swag Shots", "we", "us", or "our" mean the Swag Shots service operated by {businessInfo.operatorName}.
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">Registered Business:</span> {businessInfo.operatorName}
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">GSTIN:</span> {businessInfo.gstin}
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">Business Type:</span> {businessInfo.constitution}
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">Registered Address:</span> {businessInfo.registeredAddress}
                                    </p>
                                </div>
                            </section>

                            {/* Section 1 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    1. Acceptance
                                </h2>
                                <div className="bg-primary-50 rounded-lg p-6">
                                    <p className="text-gray-700 leading-relaxed mb-4">
                                        By creating an account, accessing, or posting content, you agree to these Terms, our Community Guidelines, and any additional terms (e.g., Creator Monetization Terms, Gift/Wallet Policies).
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        We may modify these Terms. If material changes are made, we will notify you in-app, via email, or by updating the "Effective Date." Continued use means acceptance.
                                    </p>
                                </div>
                            </section>

                            {/* Section 2 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    2. Eligibility
                                </h2>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                <strong>Minimum age:</strong> 13 years old (or higher if local law requires)
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                Users under 18 years must have parental or guardian consent where required
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                Minors' accounts receive extra protections (content visibility limits, stricter reporting)
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                You may not use Swag Shots if prohibited by applicable law, sanctions, or app store rules
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Section 3 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    3. Your Account
                                </h2>
                                <div className="bg-green-50 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Provide accurate information during registration and keep credentials secure
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            You are responsible for all activity on your account
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Impersonation, fake accounts, or misleading info may result in suspension
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            You may delete your account at any time through in-app settings or by contacting support
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 4 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    4. Content on Swag Shots
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        <strong>"User Content"</strong> includes: Photos, videos, reels, livestreams, stories, music, captions, messages, comments, and profile info.
                                    </p>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                                            <h4 className="text-lg font-semibold text-green-700 mb-4">Your Rights</h4>
                                            <ul className="space-y-2 text-gray-700">
                                                <li>• You retain ownership of your content</li>
                                                <li>• By posting, you grant Swag Shots a non-exclusive, royalty-free, worldwide, transferable, sublicensable license to host, display, promote, distribute, and adapt content for operation of the Service</li>
                                            </ul>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                                            <h4 className="text-lg font-semibold text-red-700 mb-4">Your Responsibilities</h4>
                                            <ul className="space-y-2 text-gray-700">
                                                <li>• You must have all rights and permissions for your content</li>
                                                <li>• Content must not violate copyright, law, or third-party rights</li>
                                                <li>• Prohibited: illegal, pornographic, violent, hateful, exploitative, terrorist, or misleading material</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="bg-red-100 border-2 border-red-300 rounded-lg p-6">
                                        <p className="text-red-800 font-bold text-center">
                                            <span className="text-2xl mr-2">🚫</span>
                                            Zero Tolerance: Exploitation or grooming of minors = immediate ban + legal action.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Section 5 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    5. Creator Features & Monetization
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed font-medium">
                                        Swag Shots allows creators to:
                                    </p>
                                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6">
                                        <ul className="space-y-2">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Post photos, stories, reels, livestreams
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Receive virtual gifts (coins, stickers, etc.)
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Earn via Creator Monetization Programs (ads, tips, subscriptions, boosts)
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                                        <h4 className="text-lg font-semibold text-orange-800 mb-4">Monetization Rules</h4>
                                        <ul className="space-y-2 text-gray-700">
                                            <li>• Participation requires separate Creator Terms, KYC/ID verification, and payout setup</li>
                                            <li>• Payouts depend on Swag Shots's final internal ad reports & reconciliation</li>
                                            <li>• Minimum thresholds, fees, and applicable taxes apply</li>
                                            <li>• Swag Shots may withhold or reverse earnings for fraud, fake traffic, policy violations, or suspicious activity</li>
                                            <li>• Virtual coins/gifts have no cash value except as permitted by Creator Terms</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Section 6 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    6. Prohibited Conduct
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed font-medium">
                                        You agree NOT to:
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                                            <ul className="space-y-3">
                                                <li className="text-gray-700 flex items-start">
                                                    <span className="text-red-600 mr-3 mt-1">✗</span>
                                                    Post harmful, harassing, obscene, exploitative, or misleading content
                                                </li>
                                                <li className="text-gray-700 flex items-start">
                                                    <span className="text-red-600 mr-3 mt-1">✗</span>
                                                    Use Swag Shots for spam, scams, chain messages, phishing, or unlawful commercial activity
                                                </li>
                                                <li className="text-gray-700 flex items-start">
                                                    <span className="text-red-600 mr-3 mt-1">✗</span>
                                                    Interfere with the Service (hacks, reverse-engineering, bots, fake engagement)
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                                            <ul className="space-y-3">
                                                <li className="text-gray-700 flex items-start">
                                                    <span className="text-red-600 mr-3 mt-1">✗</span>
                                                    Abuse ad systems (fake clicks, incentivized traffic outside reward ads)
                                                </li>
                                                <li className="text-gray-700 flex items-start">
                                                    <span className="text-red-600 mr-3 mt-1">✗</span>
                                                    Violate intellectual property rights
                                                </li>
                                                <li className="text-gray-700 flex items-start">
                                                    <span className="text-red-600 mr-3 mt-1">✗</span>
                                                    Circumvent safety measures or content moderation
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section 7 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    7. Content Moderation & Enforcement
                                </h2>
                                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-purple-600 mr-3 mt-1">•</span>
                                                We have no tolerance for objectionable content or abusive users
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-purple-600 mr-3 mt-1">•</span>
                                                Swag Shots uses automated systems and human reviewers to enforce policies
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-purple-600 mr-3 mt-1">•</span>
                                                Users can report objectionable content and block abusive users directly in the app
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-purple-600 mr-3 mt-1">•</span>
                                                We review safety reports and act on valid objectionable-content reports within 24 hours, including content removal and account suspension or termination where appropriate
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-purple-600 mr-3 mt-1">•</span>
                                                We may remove content, limit distribution, or suspend accounts for violations
                                            </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">•</span>
                                            Appeals can be submitted through in-app reporting mechanisms
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">•</span>
                                            Repeat violations may result in permanent account termination
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 8 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    8. Intellectual Property
                                </h2>
                                <div className="bg-indigo-50 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            Swag Shots respects intellectual property rights
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            DMCA takedown requests can be submitted to:{' '}
                                            <a href={`mailto:${businessInfo.email}`} className="text-primary-600 hover:text-primary-700 underline ml-1">
                                                info@swagshots.com
                                            </a>
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            Counter-notifications follow standard DMCA procedures
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            Repeat copyright infringers will be terminated
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 9 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    9. Privacy & Data Protection
                                </h2>
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                    <p className="text-gray-700 leading-relaxed">
                                        Your privacy is governed by our{' '}
                                        <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700 underline font-semibold">
                                            Privacy Policy
                                        </Link>, which explains:
                                    </p>
                                    <ul className="space-y-2 mt-4">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">•</span>
                                            What data we collect and how we use it
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">•</span>
                                            Your rights and choices regarding personal data
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">•</span>
                                            Special protections for children under 18
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">•</span>
                                            International data transfers and security measures
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 10 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    10. Payments, Refunds & Cancellations
                                </h2>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                    <p className="text-gray-700 leading-relaxed mb-4">
                                        See our dedicated{' '}
                                        <Link to="/refund-policy" className="text-primary-600 hover:text-primary-700 underline font-semibold">
                                            Cancellation & Refund Policy
                                        </Link>{' '}
                                        for refund eligibility, cancellation rules, and processing timelines.
                                    </p>
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Purchases are currently unavailable in the iOS app. If iOS digital items or paid features are offered in the future, they will be processed through Apple's in-app purchase system as required by Apple App Review Guidelines
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Eligible Android, web, or other non-iOS payment flows may be processed by app stores or approved payment gateway partners under the merchant name {businessInfo.operatorName}
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            You authorize charges when you subscribe, recharge, buy virtual items, promote content, or purchase any paid feature
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            All prices include applicable taxes where required
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Refunds for failed, duplicate, or wrongly charged payments can be requested at {businessInfo.email}. Approved refunds are normally initiated within 7 business days, subject to payment partner or app store processing time
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Used virtual coins, gifts, boosts, subscriptions, creator tools, and other consumed digital benefits are generally non-refundable unless required by law or platform policy
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Creator payouts, reward eligibility, and withdrawals are separate from user purchase refunds and may require KYC, tax verification, fraud review, and minimum payout thresholds
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 11 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    11. Disclaimers & Limitations
                                </h2>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-yellow-600 mr-3 mt-1">•</span>
                                            Swag Shots is provided "AS IS" without warranties
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-yellow-600 mr-3 mt-1">•</span>
                                            We do not guarantee uninterrupted service or error-free operation
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-yellow-600 mr-3 mt-1">•</span>
                                            User-generated content reflects individual opinions, not Swag Shots's views
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-yellow-600 mr-3 mt-1">•</span>
                                            Our liability is limited to the maximum extent permitted by law
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 12 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    12. Termination
                                </h2>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-red-600 mr-3 mt-1">•</span>
                                            You may delete your account at any time
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-red-600 mr-3 mt-1">•</span>
                                            We may suspend or terminate accounts for policy violations
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-red-600 mr-3 mt-1">•</span>
                                            Upon termination, your right to use Swag Shots ends immediately
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-red-600 mr-3 mt-1">•</span>
                                            Residual copies of content may remain in backups for a reasonable period
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 13 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    13. Governing Law & Disputes
                                </h2>
                                <div className="bg-indigo-50 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            These Terms are governed by the laws of India, with courts having jurisdiction over the registered business location of {businessInfo.operatorName}
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            Disputes should first be raised with our support team
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            Arbitration may apply where permitted by law
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-indigo-600 mr-3 mt-1">•</span>
                                            You waive the right to participate in class action lawsuits
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 14 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    14. Changes to Terms
                                </h2>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            We may update these Terms periodically
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Material changes will be communicated through in-app notifications
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-green-600 mr-3 mt-1">•</span>
                                            Continued use after notice constitutes acceptance
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 15 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    15. Contact Information
                                </h2>
                                <div className="bg-primary-50 rounded-lg p-6 text-center">
                                    <p className="text-gray-700 leading-relaxed">
                                        <strong>All Inquiries:</strong>{' '}
                                        <a href={`mailto:${businessInfo.email}`} className="text-primary-600 hover:text-primary-700 underline font-semibold">
                                            {businessInfo.email}
                                        </a>
                                    </p>
                                    <p className="text-gray-600 text-sm mt-2">
                                        We handle all support, privacy, legal, and copyright matters through our main contact email.
                                    </p>
                                </div>
                            </section>
                        </div>

                        {/* Contact Box */}
                        <div className="bg-gradient-to-br from-primary-600 to-primary-500 text-white rounded-lg p-6 mt-8 text-center">
                            <h3 className="text-xl font-semibold mb-3">Questions About These Terms?</h3>
                            <p className="mb-4">
                                <strong>Contact us:</strong>{' '}
                                <a href={`mailto:${businessInfo.email}`} className="text-white underline hover:no-underline">
                                    {businessInfo.email}
                                </a>
                            </p>
                            <p className="text-primary-100 text-sm">
                                We're here to help explain any part of our terms and conditions.
                            </p>
                        </div>

                        <div className="text-center text-gray-500 mt-8">
                            <p>
                                <strong>Swag Shots - Create. Connect. Earn.</strong><br />
                                Operated by {businessInfo.operatorName}. GSTIN: {businessInfo.gstin}<br />
                                By using our service, you agree to these terms.
                            </p>
                        </div>

                        {/* Back to Home */}
                        <div className="text-center mt-8">
                            <Link
                                to="/"
                                className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                            >
                                ← Back to Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
